import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useWishList } from '../../contexts/WishListContext';
import {
  productAttributeLabelDictionnary,
  productAttributeValueDictionnary,
  getFailoverImage,
  getProductLinkFromProduct,
} from '../../utils/productHelper';
import PropTypes from 'prop-types';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  CurrentRefinements,
  Menu,
  RangeInput,
  RefinementList,
  ToggleRefinement,
  usePagination,
} from 'react-instantsearch';
import { useEffect, useMemo, useRef, useState } from 'react';
import Amount from '../Amount/Amount';
import TyreFilter from '../commons/TyreFilter/TyreFilter';
import Tag from '../Tag';
import {
  estimatedDeliveryDateToDisplay,
  numberToDisplay,
} from '../../utils/formatting';
import { PRIMARY_BRANDS } from '../../helpers/shopHelper';
import { HiOutlineTruck } from 'react-icons/hi2';
import { useShop } from '../../contexts/ShopContext';

export const Product = ({
  title = 'Title',
  price = 0,
  liked = false,
  src = '',
  id = '',
  type,
  product,
}) => {
  let failover = 'failover.png';
  if (typeof src !== 'string') src = '';
  const { addToWishList, isItemInWishList, removeFromWishList } = useWishList();
  const navigate = useNavigate();

  const handleWishList = (product, event) => {
    event.preventDefault();
    if (isItemInWishList(product)) {
      removeFromWishList(product);
    } else {
      addToWishList(product);
    }
  };
  const productLink = getProductLinkFromProduct(product);

  const isVehicle = type === 'vehicules';
  const forParts = product.procedure === 'PIECES';
  const isMecaPart = type === 'pieces' && product.carmeca === 'M';

  failover = getFailoverImage(type, product.partCode);

  const { getEstimatedDeliveryDate } = useShop();

  const estimatedDeliveryDate = getEstimatedDeliveryDate(product);

  return (
    <Link
      to={productLink}
      state={{
        from: {
          pathname: window.location.pathname,
          search: window.location.search,
        },
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(productLink, {
          state: {
            from: {
              pathname: window.location.pathname,
              search: window.location.search,
            },
          },
        });
      }}
      className="w-full h-full flex flex-col gap-[16px] justify-between cursor-pointer relative"
    >
      <div
        className={`w-full flex flex-col gap-[16px] relative ${
          product.availability === 'unavailable' && '[&>*]:opacity-50'
        }`}
      >
        {isVehicle && forParts && (
          <Tag.Container className="!opacity-100">
            <Tag.ForParts />
          </Tag.Container>
        )}
        {product.availability === 'unavailable' && (
          <span className="sold_out !opacity-100">Vendu {forParts}</span>
        )}
        <img
          src={
            src.substring(
              0,
              src.indexOf('?lock') != -1 ? src.indexOf('?lock') : src.length,
            ) +
            '?lock=' +
            id
          }
          onError={({ currentTarget }) => {
            currentTarget.onerror = null; // prevents looping
            currentTarget.src = '/' + failover;
          }}
          alt=""
          className="w-full h-[200px] sm:h-[250px] aspect-square object-cover rounded-[6px]"
        />
        <div className="flex flex-col w-full gap-[4px]">
          <div className="w-full flex items-start justify-between">
            <div className="font-[600] text-[14px] sm:text-[16px] 1200:text-[20px] 1200:leading-[28px] text-dark">
              <p>{title}</p>
            </div>
            <div
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleWishList(product, event);
              }}
            >
              {isItemInWishList(product) ? (
                <img
                  src="/pictos/liked.png"
                  alt=""
                  className="max-w-[20px] sm:max-w-[21px] max-h-[18.6px] cursor-pointer"
                />
              ) : (
                <img
                  src="/pictos/unliked.png"
                  alt=""
                  className="max-w-[20px] sm:max-w-[21px] max-h-[18.6px] opacity-[.6] cursor-pointer"
                />
              )}
            </div>
          </div>
          <div className="font-[600] text-[14px] sm:text-[16px] 1200:text-[20px] 1200:leading-[24px] text-blue flex justify-between">
            {!!price && Number(price) && (
              <span>
                <Amount value={Number(price)} precision={0} />
              </span>
            )}
            {isMecaPart && product.mileage > 5 && (
              <span className="font-[300] text-[14px] sm:text-[16px] 1200:text-[18px] 1200:leading-[22px] text-dark">
                {numberToDisplay(product.mileage)} km
              </span>
            )}
          </div>
        </div>
      </div>
      {estimatedDeliveryDate && product.availability !== 'unavailable' && (
        <div className="flex items-center gap-3 p-2.5 bg-gray/50 rounded-lg ml-[5px]">
          <HiOutlineTruck size={22} className="text-orange flex-shrink-0" />
          <div className="text-[13px] sm:text-[14px] text-dark/80">
            Livraison estimée pour le{' '}
            <span className="font-semibold text-dark">
              {estimatedDeliveryDateToDisplay(estimatedDeliveryDate)}
            </span>
          </div>
        </div>
      )}
    </Link>
  );
};

Product.propTypes = {
  title: PropTypes.string,
  src: PropTypes.string,
  price: PropTypes.number || PropTypes.string,
  liked: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  product: PropTypes.object,
  type: PropTypes.string,
};

export const Panel = ({
  children,
  header = null,
  hidden = false,
  footer = null,
}) => {
  return (
    <div hidden={hidden}>
      {header && (
        <h1 className="font-clash text-[15px] sm:text-[20px] my-3 leading-[1] font-[600] text-orange">
          {header}
        </h1>
      )}
      <div className="ais-Panel-body">{children}</div>
      {footer && <div className="ais-Panel-footer">{footer}</div>}
    </div>
  );
};

export const StatePagination = ({ hitsPerPage }) => {
  const { currentRefinement, nbPages, nbHits } = usePagination();
  return (
    <div>
      <span>
        {Math.min((currentRefinement + 1) * hitsPerPage, nbHits)} sur{' '}
        {nbHits === 1000 ? '1000+' : nbHits}
      </span>
    </div>
  );
};

export const RefinementListDropDown = ({
  attribute,
  placeholder,
  currentValue = null,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
      >
        {placeholder}
        <ChevronDownIcon
          className="w-5 h-5 ml-2 -mr-1 text-gray-400"
          aria-hidden="true"
        />
      </button>

      <div
        className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg"
        hidden={!isOpen}
      >
        <div className="py-1">
          <MyRefinementList
            attribute={attribute}
            {...props}
            limit={999999999}
            classNames={{
              list: '!max-h-60 space-y-1',
              item: 'flex items-center px-4 py-1 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900',
              label: 'flex items-center w-full',
              checkbox:
                'mr-2 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded',
              labelText: 'flex-grow',
              count: 'ml-auto text-xs text-gray-500',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const MyRefinementList = ({
  customSortBy = 'label:asc',
  priorityValues = [],
  currentFilters = {},
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resultsLength, setResultsLength] = useState(0);
  const [isLoadingFacets, setIsLoadingFacets] = useState(false);

  const enableCustomSearch = true;

  if (enableCustomSearch && props.searchable) {
    useEffect(() => {
      if (!searchValue.trim()) {
        setResultsLength(0);
        setDebouncedSearchValue('');
        setIsLoadingFacets(false);
        return;
      }
      setIsLoadingFacets(true);
      const timer = setTimeout(() => {
        setIsLoadingFacets(false);
        setDebouncedSearchValue(searchValue);
      }, 1000);

      return () => {
        clearTimeout(timer);
      };
    }, [searchValue]);

    useEffect(() => {
      const timer = setTimeout(() => {
        setIsLoading(debouncedSearchValue !== searchValue);
      }, 300);

      return () => {
        clearTimeout(timer);
      };
    }, [debouncedSearchValue, searchValue]);
  }

  const sortWithPriorityValues = (a, b, sort = customSortBy) => {
    const aLabel = a.label ?? a.name;
    const bLabel = b.label ?? b.name;

    if (a.isRefined && !b.isRefined) return -1;
    if (!a.isRefined && b.isRefined) return 1;

    if (priorityValues.includes(aLabel) && priorityValues.includes(bLabel)) {
      return priorityValues.indexOf(aLabel) - priorityValues.indexOf(bLabel);
    }
    if (priorityValues.includes(aLabel)) return -1;
    if (priorityValues.includes(bLabel)) return 1;

    if (sort === 'label:asc') {
      const numA = parseInt(aLabel);
      const numB = parseInt(bLabel);

      if (!isNaN(numA) && !isNaN(numB) && numA !== numB) {
        return numA - numB;
      }

      return aLabel.localeCompare(bLabel);
    }
    if (sort === 'label:desc') {
      const numA = parseInt(aLabel);
      const numB = parseInt(bLabel);

      if (!isNaN(numA) && !isNaN(numB) && numA !== numB) {
        return numB - numA;
      }

      return bLabel.localeCompare(aLabel);
    }
    if (sort === 'count:asc') {
      return a.count - b.count;
    }
    if (sort === 'count:desc') {
      return b.count - a.count;
    }
  };

  const transformItems = useMemo(
    () => (items) => {
      items = items.map((item) => ({
        ...item,
        label:
          productAttributeValueDictionnary[props.attribute]?.[item.label] ??
          item.label,
      }));

      if (
        debouncedSearchValue &&
        !isLoadingFacets &&
        enableCustomSearch &&
        props.searchable
      ) {
        items = items.filter((item) =>
          item.label.toLowerCase().includes(debouncedSearchValue.toLowerCase()),
        );

        // sort if contains the search value
        items = items.sort((a, b) => {
          return a.label
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase())
            ? -1
            : 1;
        });

        // sort if start with the search value
        items = items.sort((a, b) => {
          return a.label
            .toLowerCase()
            .startsWith(debouncedSearchValue.toLowerCase())
            ? -1
            : 1;
        });

        // sort if is the same as the search value
        items = items.sort((a, b) => {
          return a.label === debouncedSearchValue ? -1 : 1;
        });
      } else {
        items = items.sort((a, b) => sortWithPriorityValues(a, b));
      }
      items = props.transformItems ? props.transformItems(items) : items;
      setResultsLength(items.length);
      return items;
    },
    [props.transformItems, debouncedSearchValue, isLoadingFacets],
  );
  const sortBy = useMemo(() => (a, b) => sortWithPriorityValues(a, b), []);

  return (
    <>
      {props.searchable && enableCustomSearch && (
        <div className="ais-RefinementList-searchBox">
          <div className="ais-SearchBox">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className="ais-SearchBox-form"
              noValidate={true}
              role="search"
            >
              <input
                className="ais-SearchBox-input"
                aria-label="Search"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                placeholder={props.searchablePlaceholder}
                spellCheck={false}
                maxLength={512}
                type="search"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
              />
              <button
                className="ais-SearchBox-submit"
                type="submit"
                title="Submit the search query"
              >
                <svg
                  className="ais-SearchBox-submitIcon"
                  width="10"
                  height="10"
                  viewBox="0 0 40 40"
                  aria-hidden="true"
                >
                  <path d="M26.804 29.01c-2.832 2.34-6.465 3.746-10.426 3.746C7.333 32.756 0 25.424 0 16.378 0 7.333 7.333 0 16.378 0c9.046 0 16.378 7.333 16.378 16.378 0 3.96-1.406 7.594-3.746 10.426l10.534 10.534c.607.607.61 1.59-.004 2.202-.61.61-1.597.61-2.202.004L26.804 29.01zm-10.426.627c7.323 0 13.26-5.936 13.26-13.26 0-7.32-5.937-13.257-13.26-13.257C9.056 3.12 3.12 9.056 3.12 16.378c0 7.323 5.936 13.26 13.258 13.26z"></path>
                </svg>
              </button>
              <button
                className="ais-SearchBox-reset"
                type="reset"
                title="Clear the search query"
                hidden={
                  !debouncedSearchValue ||
                  !searchValue ||
                  isLoading ||
                  isLoadingFacets
                }
                onClick={() => {
                  setSearchValue('');
                  setDebouncedSearchValue('');
                }}
              >
                <svg
                  className="ais-SearchBox-resetIcon"
                  viewBox="0 0 20 20"
                  width="10"
                  height="10"
                >
                  <path d="M8.114 10L.944 2.83 0 1.885 1.886 0l.943.943L10 8.113l7.17-7.17.944-.943L20 1.886l-.943.943-7.17 7.17 7.17 7.17.943.944L18.114 20l-.943-.943-7.17-7.17-7.17 7.17-.944.943L0 18.114l.943-.943L8.113 10z"></path>
                </svg>
              </button>
              <span
                className="ais-SearchBox-loadingIndicator"
                hidden={!isLoading && !isLoadingFacets}
              >
                <svg
                  aria-label="Results are loading"
                  width="16"
                  height="16"
                  viewBox="0 0 38 38"
                  stroke="#444"
                  className="ais-SearchBox-loadingIcon"
                >
                  <g fill="none" fillRule="evenodd">
                    <g transform="translate(1 1)" strokeWidth="2">
                      <circle
                        strokeOpacity=".5"
                        cx="18"
                        cy="18"
                        r="18"
                      ></circle>
                      <path d="M36 18c0-9.94-8.06-18-18-18">
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          from="0 18 18"
                          to="360 18 18"
                          dur="1s"
                          repeatCount="indefinite"
                        ></animateTransform>
                      </path>
                    </g>
                  </g>
                </svg>
              </span>
            </form>
          </div>
        </div>
      )}
      {enableCustomSearch &&
        props.searchable &&
        resultsLength === 0 &&
        !isLoadingFacets && (
          <div className="my-refinement-list">
            <p className="text-sm mb-3">Aucun résultat</p>
          </div>
        )}
      <RefinementList
        translations={{
          showMoreButtonText({ isShowingMore }) {
            return isShowingMore ? 'Voir moins' : 'Voir plus';
          },
          noResultsText: 'Aucun résultat',
        }}
        showMoreLimit={999999999}
        className="my-refinement-list"
        transformItems={transformItems}
        sortBy={sortBy}
        // sortBy={[customSortBy, 'count:desc']}
        {...props}
        searchable={enableCustomSearch ? false : props.searchable}
        limit={debouncedSearchValue ? 99999999 : props.limit ?? 10}
      />
    </>
  );
};

export const MyMenu = ({
  priorityValues = [],
  customSortBy = 'label:asc',
  ...props
}) => {
  const sortWithPriorityValues = (a, b, sort = customSortBy) => {
    const aLabel = a.label ?? a.name;
    const bLabel = b.label ?? b.name;

    if (a.isRefined && !b.isRefined) return -1;
    if (!a.isRefined && b.isRefined) return 1;

    if (priorityValues.includes(aLabel) && priorityValues.includes(bLabel)) {
      return priorityValues.indexOf(aLabel) - priorityValues.indexOf(bLabel);
    }
    if (priorityValues.includes(aLabel)) return -1;
    if (priorityValues.includes(bLabel)) return 1;

    if (sort === 'label:asc') {
      const numA = parseInt(aLabel);
      const numB = parseInt(bLabel);

      if (!isNaN(numA) && !isNaN(numB) && numA !== numB) {
        return numA - numB;
      }

      return aLabel.localeCompare(bLabel);
    }
    if (sort === 'label:desc') {
      const numA = parseInt(aLabel);
      const numB = parseInt(bLabel);

      if (!isNaN(numA) && !isNaN(numB) && numA !== numB) {
        return numB - numA;
      }

      return bLabel.localeCompare(aLabel);
    }
    if (sort === 'count:asc') {
      return a.count - b.count;
    }
    if (sort === 'count:desc') {
      return b.count - a.count;
    }
  };

  const transformItems = useMemo(
    () => (items) => {
      items = items.map((item) => ({
        ...item,
        label:
          productAttributeValueDictionnary[props.attribute]?.[item.label] ??
          item.label,
      }));
      items = items.sort((a, b) => sortWithPriorityValues(a, b));

      return props.transformItems ? props.transformItems(items) : items;
    },
    [props.transformItems],
  );

  const sortBy = useMemo(() => (a, b) => sortWithPriorityValues(a, b), []);

  return <Menu {...props} transformItems={transformItems} sortBy={sortBy} />;
};

export const MyCurrentRefinements = () => {
  const location = useLocation();

  return (
    <CurrentRefinements
      transformItems={(items) => {
        if (
          location.pathname === '/vehicules-d-occasion' ||
          location.pathname === '/vehicules-accidentes'
        ) {
          items = items.filter((item) => item.label !== 'type');
        }
        items.forEach((item) => {
          item.label =
            productAttributeLabelDictionnary[item.label] ?? item.label;
          item.refinements.forEach((refinement) => {
            refinement.label =
              productAttributeValueDictionnary[item.attribute]?.[
                refinement.label
              ] ?? refinement.label;
          });
        });
        return items;
      }}
    />
  );
};

export function instantSearchOnStateChange(
  { uiState, setUiState },
  setCurrentFilters,
  [currentPage, setCurrentPage],
) {
  const correspondanceGlobalType = {
    Véhicules: 'vehicules',
    Pneus: 'tyres',
    Jantes: 'rims',
    'Pièces détachées': 'parts',
  };

  const index = Object.keys(uiState)[0];
  const codeType =
    index.split('_')[0] === 'products'
      ? correspondanceGlobalType[uiState[index]?.menu?.globalType]
      : index.split('_')[0];
  // delete model filter when gamme filter is empty for vehicules & parts
  if (
    (codeType === 'vehicules' || codeType === 'parts') &&
    !uiState[index]?.refinementList?.gamme &&
    uiState[index]?.refinementList?.model
  ) {
    delete uiState[index].refinementList.model;
  }
  if (
    !codeType &&
    !uiState[index]?.refinementList?.brand &&
    uiState[index]?.refinementList?.globalModel
  ) {
    delete uiState[index].refinementList.globalModel;
  }

  setCurrentFilters({
    ...uiState[index]?.refinementList,
    ...uiState[index]?.menu,
  });

  const newPage = uiState[index]?.page ?? 1;
  if (newPage !== currentPage) {
    window.scrollTo(0, 0);
    setCurrentPage(newPage);
  }

  setUiState(uiState);
}

export const TyreFilters = ({ currentFilters = {}, hidden = false }) => {
  return (
    <div hidden={hidden}>
      <TyreFilter
        widthValue={currentFilters?.width ?? undefined}
        heightValue={currentFilters?.height ?? undefined}
        diameterValue={currentFilters?.diameter ?? undefined}
        loadspeedValue={currentFilters?.speedLoadIndex ?? undefined}
      />
      <Panel header="Article">
        <MyRefinementList
          attribute="partLabel"
          searchable={false}
          searchablePlaceholder="Article"
          showMore={false}
          operator="and"
          currentFilters={currentFilters}
        />
      </Panel>

      <Panel header="Marque">
        <MyRefinementList
          attribute="brand"
          searchable={true}
          searchablePlaceholder="Marque"
          showMore={true}
          operator="and"
          priorityValues={PRIMARY_BRANDS}
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Prix">
        <RangeInput
          attribute="price"
          precision={0}
          translations={{
            submitButtonText: 'Valider',
            separatorElementText: ' à ',
          }}
        />
      </Panel>
      <Panel header="Saison">
        <MyRefinementList
          attribute="season"
          showMore={true}
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Etat">
        <MyRefinementList attribute="status" currentFilters={currentFilters} />
      </Panel>
    </div>
  );
};

export const RimFilters = ({ currentFilters = {}, hidden = false }) => {
  return (
    <div hidden={hidden}>
      <Panel header="Article">
        <MyRefinementList
          attribute="partLabel"
          searchable={true}
          searchablePlaceholder="Article"
          showMore={true}
          operator="and"
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Marque">
        <MyRefinementList
          attribute="brand"
          operator="and"
          searchable={true}
          searchablePlaceholder="Marque"
          showMore={true}
          priorityValues={PRIMARY_BRANDS}
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Modèle">
        <MyRefinementList
          attribute="familyModel"
          searchable={true}
          searchablePlaceholder="Modèle"
          showMore={true}
          operator="and"
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Diamètre">
        <MyRefinementList
          attribute="diameter"
          operator="or"
          showMore={true}
          customSortBy="label:asc"
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Largeur">
        <MyRefinementList
          attribute="width"
          showMore={true}
          operator="or"
          customSortBy="label:asc"
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Nombre de trous">
        <MyRefinementList
          attribute="numberOfHoles"
          searchable={false}
          operator="and"
          searchablePlaceholder="Nombre de trous"
          showMore={false}
          customSortBy="label:asc"
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Prix">
        <RangeInput
          attribute="price"
          precision={0}
          translations={{
            submitButtonText: 'Valider',
            separatorElementText: ' à ',
          }}
        />
      </Panel>
      <Panel header="Déport de jante (ET)">
        <MyRefinementList
          attribute="deportET"
          showMore={true}
          operator="and"
          currentFilters={currentFilters}
        />
      </Panel>
    </div>
  );
};

export const VehicleFilters = ({ currentFilters = {}, hidden = false }) => {
  return (
    <div hidden={hidden}>
      {window.location.pathname !== '/vehicules-d-occasion' &&
        window.location.pathname !== '/vehicules-accidentes' && (
          <Panel header="Etat">
            <MyMenu
              attribute="type"
              priorityValues={['Occasion', 'Accidenté']}
            />
          </Panel>
        )}
      <Panel header="Marque">
        <MyRefinementList
          attribute="brand"
          operator="and"
          searchable={true}
          searchablePlaceholder="Marque"
          showMore={true}
          priorityValues={PRIMARY_BRANDS}
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Gamme">
        <MyRefinementList
          attribute="gamme"
          searchable={true}
          searchablePlaceholder="Gamme"
          showMore={true}
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Modèle" hidden={!currentFilters?.gamme}>
        <MyRefinementList
          attribute="model"
          searchable={true}
          searchablePlaceholder="Modèle"
          showMore={true}
          operator="and"
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Type">
        <ToggleRefinement attribute="is2Wheels" label="2 roues" on="1" />
      </Panel>
      <Panel header="Transmission">
        <MyRefinementList
          attribute="transmission"
          searchablePlaceholder="Transmission"
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Procédure">
        <MyRefinementList
          attribute="procedure"
          showMore={true}
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Voiture sans permis">
        <ToggleRefinement
          attribute="withoutLicense"
          label="Voiture sans permis"
          on="1"
        />
      </Panel>
      <Panel header="Année">
        <MyRefinementList
          attribute="year"
          searchable={true}
          searchablePlaceholder="Année"
          showMore={true}
          customSortBy="label:desc"
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Motorisation">
        <MyRefinementList
          attribute="energy"
          searchable={false}
          operator="or"
          searchablePlaceholder="Motorisation"
          showMore={true}
          showMoreLimit={100000}
          limit={10}
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Prix">
        <RangeInput
          attribute="price"
          precision={0}
          translations={{
            submitButtonText: 'Valider',
            separatorElementText: ' à ',
          }}
        />
      </Panel>
    </div>
  );
};

export const PartFilters = ({ currentFilters = {}, hidden = false }) => {
  return (
    <div hidden={hidden}>
      <Panel header="Article">
        <MyRefinementList
          attribute="partLabel"
          searchable={true}
          searchablePlaceholder="Article"
          showMore={true}
          operator="and"
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Marque">
        <MyRefinementList
          attribute="brand"
          operator="and"
          searchable={true}
          searchablePlaceholder="Marque"
          showMore={true}
          priorityValues={PRIMARY_BRANDS}
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Gamme">
        <MyRefinementList
          attribute="gamme"
          searchable={true}
          searchablePlaceholder="Gamme"
          showMore={true}
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Modèle" hidden={!currentFilters?.gamme}>
        <MyRefinementList
          attribute="model"
          searchable={true}
          searchablePlaceholder="Modèle"
          showMore={true}
          operator="and"
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Motorisation">
        <MyRefinementList
          attribute="energy"
          searchable={false}
          operator="or"
          searchablePlaceholder="Motorisation"
          showMore={true}
          showMoreLimit={100000}
          limit={10}
          currentFilters={currentFilters}
        />
      </Panel>
      <Panel header="Prix">
        <RangeInput
          attribute="price"
          precision={0}
          translations={{
            submitButtonText: 'Valider',
            separatorElementText: ' à ',
          }}
        />
      </Panel>
    </div>
  );
};
